import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ConfirmDialog from "../../../components/ui/ConfirmDialog";
import { Badge, Segmented, Switch } from "../../../components/ui";
import SegmentCriteria, { criteriaOf } from "./SegmentCriteria";
import {
  apiMessage,
  countAudience,
  searchNotificationUsers,
  searchNotificationVideos,
  sendNotification,
} from "../../../services/notifications";

export const TITLE_MAX = 65;
export const BODY_MAX = 240;

export const EMPTY_DRAFT = {
  kind: "simple",
  title: "",
  body: "",
  withEnglish: false,
  title_en: "",
  body_en: "",
  imageUrl: "",
  link: "",
  video: null,
  audienceType: "all",
  users: [],
  plan: "any",
  activity: "any",
  days: 30,
  donors: false,
  quiz: false,
  language: "",
  countries: [],
  saveToInbox: true,
  when: "now",
  scheduledAt: "",
  // Day of high traffic: sent in waves instead of all at once
  spread: false,
};

/** Draft from a sent notification (the "Duplicate" action of the history). */
export function draftFrom(notification) {
  const audience = notification.audience || {};
  return {
    ...EMPTY_DRAFT,
    kind: notification.kind === "video" ? "video" : "simple",
    title: notification.title || "",
    body: notification.body || "",
    withEnglish: Boolean(notification.title_en),
    title_en: notification.title_en || "",
    body_en: notification.body_en || "",
    imageUrl: notification.imageUrl || "",
    link: notification.link || "",
    video: notification.video?.id ? notification.video : null,
    audienceType: audience.type || "all",
    users: (audience.userIds || [])
      .filter((u) => u && typeof u === "object")
      .map((u) => ({
        id: u._id,
        name: [u.name, u.last_name].filter(Boolean).join(" ") || u.email,
        email: u.email,
        reachable: true,
      })),
    ...criteriaOf(audience.segment),
    saveToInbox: notification.saveToInbox !== false,
    spread: Boolean(notification.spread?.enabled),
  };
}

const audienceOf = (draft) => ({
  type: draft.audienceType,
  userIds: draft.audienceType === "users" ? draft.users.map((u) => u.id) : [],
  segment:
    draft.audienceType === "segment"
      ? criteriaOf(draft)
      : undefined,
});

/** Request body of a draft. */
export function payloadOf(draft) {
  return {
    kind: draft.kind,
    title: draft.title.trim(),
    body: draft.body.trim(),
    title_en: draft.withEnglish ? draft.title_en.trim() : "",
    body_en: draft.withEnglish ? draft.body_en.trim() : "",
    imageUrl: draft.imageUrl.trim(),
    link: draft.kind === "simple" ? draft.link.trim() : "",
    videoId: draft.kind === "video" ? draft.video?.id : undefined,
    audience: audienceOf(draft),
    saveToInbox: draft.saveToInbox,
    spread: draft.spread === true,
    scheduledAt:
      draft.when === "later" && draft.scheduledAt
        ? new Date(draft.scheduledAt).toISOString()
        : undefined,
  };
}

/** First problem preventing the draft from being sent (translation key), or null. */
export function draftError(draft) {
  if (draft.kind === "video" && !draft.video) return "Choose a video";
  if (!draft.title.trim() || !draft.body.trim()) return "Title and message are required";
  if (draft.withEnglish && (!draft.title_en.trim() || !draft.body_en.trim()))
    return "Fill in the English version or turn it off";
  if (draft.audienceType === "users" && !draft.users.length) return "Choose at least one user";
  if (draft.link.trim() && !/^https?:\/\//i.test(draft.link.trim()))
    return "The link must start with https://";
  if (draft.imageUrl.trim() && !/^https:\/\//i.test(draft.imageUrl.trim()))
    return "The image must be an https:// address";
  if (draft.when === "later") {
    const at = new Date(draft.scheduledAt);
    if (!draft.scheduledAt || Number.isNaN(at.getTime())) return "Choose the sending date";
    if (at.getTime() < Date.now() + 60 * 1000) return "The sending date must be in the future";
  }
  return null;
}

/** Value for a datetime-local input, `minutes` from now. */
const localInputValue = (minutes) => {
  const date = new Date(Date.now() + minutes * 60 * 1000);
  date.setSeconds(0, 0);
  const offset = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

function useDebounced(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

const Counter = ({ value, max }) => (
  <span className="notif-counter" data-over={value.length > max || undefined}>
    {value.length}/{max}
  </span>
);

/** Catalogue search: picks the video a notification opens. */
const VideoPicker = ({ value, onChange }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounced = useDebounced(query);

  useEffect(() => {
    if (value) return undefined;
    let current = true;
    setLoading(true);
    searchNotificationVideos(debounced).then((items) => {
      if (!current) return;
      setResults(items);
      setLoading(false);
    });
    return () => {
      current = false;
    };
  }, [debounced, value]);

  if (value) {
    return (
      <div className="notif-video-picked">
        {value.thumbnailUrl ? (
          <img className="ui-media-thumb" src={value.thumbnailUrl} alt="" />
        ) : (
          <span className="ui-media-thumb" />
        )}
        <div className="notif-video-picked-text">
          <strong>{value.title}</strong>
          {value.isShort && <Badge color="violet">Short</Badge>}
        </div>
        <button type="button" className="ui-btn ui-btn-secondary" onClick={() => onChange(null)}>
          <i className="ri-swap-line" aria-hidden="true"></i>
          {t("Change")}
        </button>
      </div>
    );
  }

  return (
    <div className="notif-picker">
      <label className="ui-search notif-picker-search">
        <i className="ri-search-line" aria-hidden="true"></i>
        <input
          type="search"
          value={query}
          placeholder={t("Search a video by title")}
          aria-label={t("Search a video by title")}
          onChange={(e) => setQuery(e.target.value)}
        />
      </label>
      <ul className="notif-picker-list" aria-busy={loading || undefined}>
        {results.map((video) => (
          <li key={video.id}>
            <button type="button" onClick={() => onChange(video)}>
              {video.thumbnailUrl ? (
                <img className="ui-media-thumb" src={video.thumbnailUrl} alt="" loading="lazy" />
              ) : (
                <span className="ui-media-thumb" />
              )}
              <span className="notif-picker-text">
                <span className="ui-media-title">{video.title || "—"}</span>
                <span className="ui-media-meta">
                  {new Date(video.createdAt).toLocaleDateString()}
                  {video.isShort ? " · Short" : ""}
                </span>
              </span>
            </button>
          </li>
        ))}
        {!loading && !results.length && <li className="notif-picker-empty">{t("No video found")}</li>}
      </ul>
    </div>
  );
};

/** User search: adds recipients to a targeted notification. */
const UserPicker = ({ value, onChange }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const debounced = useDebounced(query);

  useEffect(() => {
    let current = true;
    if (debounced.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    searchNotificationUsers(debounced).then((items) => current && setResults(items));
    return () => {
      current = false;
    };
  }, [debounced]);

  const chosen = new Set(value.map((u) => String(u.id)));
  const add = (user) => {
    if (!chosen.has(String(user.id))) onChange([...value, user]);
    setQuery("");
  };

  return (
    <div className="notif-picker">
      {value.length > 0 && (
        <div className="ui-tags notif-user-tags">
          {value.map((user) => (
            <span key={user.id} className="ui-tag">
              {user.name}
              <button
                type="button"
                aria-label={`${t("Remove")} ${user.name}`}
                onClick={() => onChange(value.filter((u) => u.id !== user.id))}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <label className="ui-search notif-picker-search">
        <i className="ri-user-search-line" aria-hidden="true"></i>
        <input
          type="search"
          value={query}
          placeholder={t("Name, email, phone or user ID")}
          aria-label={t("Name, email, phone or user ID")}
          onChange={(e) => setQuery(e.target.value)}
        />
      </label>
      {results.length > 0 && (
        <ul className="notif-picker-list">
          {results.map((user) => (
            <li key={user.id}>
              <button
                type="button"
                disabled={!user.reachable || chosen.has(String(user.id))}
                onClick={() => add(user)}
              >
                <span className="notif-avatar" aria-hidden="true">
                  {(user.name || "?").charAt(0).toUpperCase()}
                </span>
                <span className="notif-picker-text">
                  <span className="ui-media-title">{user.name}</span>
                  <span className="ui-media-meta">{user.email || user.phone}</span>
                </span>
                {user.reachable ? (
                  chosen.has(String(user.id)) ? (
                    <Badge color="green">{t("Added")}</Badge>
                  ) : (
                    <i className="ri-add-line notif-picker-add" aria-hidden="true"></i>
                  )
                ) : (
                  <Badge color="slate">{t("No app installed")}</Badge>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/** What the notification looks like on a phone lock screen. */
export const PhonePreview = ({ title, body, image }) => {
  const { t } = useTranslation();
  const time = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return (
    <div className="notif-phone" aria-label={t("Preview")}>
      <div className="notif-phone-time">{time}</div>
      <div className="notif-phone-card">
        <div className="notif-phone-head">
          <img src="/logo-main.png" alt="" />
          <span>AFO MEDIA</span>
          <span className="notif-phone-now">{t("now")}</span>
        </div>
        <div className="notif-phone-body">
          <div className="notif-phone-text">
            <strong>{title || t("Notification title")}</strong>
            <p>{body || t("Your message will appear here.")}</p>
          </div>
          {image && <img className="notif-phone-image" src={image} alt="" />}
        </div>
      </div>
    </div>
  );
};

const Composer = ({ initialDraft, onSent }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(initialDraft || EMPTY_DRAFT);
  const [reach, setReach] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const topRef = useRef(null);

  useEffect(() => {
    if (!initialDraft) return;
    setDraft(initialDraft);
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [initialDraft]);

  const set = (changes) => setDraft((d) => ({ ...d, ...changes }));

  // Reach of the audience, refreshed when it changes
  const audienceKey = JSON.stringify(audienceOf(draft));
  const debouncedAudience = useDebounced(audienceKey, 400);
  useEffect(() => {
    let current = true;
    setReach(null);
    countAudience(JSON.parse(debouncedAudience)).then((result) => current && setReach(result));
    return () => {
      current = false;
    };
  }, [debouncedAudience]);

  const pickVideo = (video) =>
    setDraft((d) => ({
      ...d,
      video,
      // Ready-to-send text for a new video, kept if already written
      title: video && !d.title.trim() ? (video.isShort ? "Nouveau short ✨" : "Nouvelle vidéo 🔥") : d.title,
      body: video && !d.body.trim() ? video.title : d.body,
      title_en: video && !d.title_en.trim() ? (video.isShort ? "New short ✨" : "New video 🔥") : d.title_en,
      body_en: video && !d.body_en.trim() ? video.title : d.body_en,
    }));

  const error = draftError(draft);
  const previewImage = draft.imageUrl.trim() || (draft.kind === "video" ? draft.video?.thumbnailUrl : "");
  const personalized = draft.audienceType !== "all";

  const reachLabel = useMemo(() => {
    if (!reach) return t("Counting…");
    if (draft.audienceType === "all")
      return t("Every device with the app (incl. {{count}} logged-in users)", { count: reach.loggedIn ?? 0 });
    return t("{{count}} device(s) will receive it", { count: reach.count ?? 0 });
  }, [reach, draft.audienceType, t]);

  // Waves planned by the API from the audience size (kept under the measured capacity)
  const plan = reach?.spreadPlan;
  const spreadLabel = plan
    ? t("{{waves}} waves of about {{size}} devices, every {{interval}} minutes (about {{minutes}} minutes in all)", {
        waves: plan.waves,
        size: new Intl.NumberFormat().format(plan.waveSize),
        interval: plan.intervalMin,
        minutes: plan.minutes,
      })
    : t("Counting…");

  const submit = () => {
    setShowErrors(true);
    if (error) return;
    setConfirming(true);
  };

  const send = async () => {
    setSending(true);
    try {
      const { notification, message } = await sendNotification(payloadOf(draft));
      if (notification.status === "scheduled") {
        toast.success(t("Notification scheduled"));
      } else if (notification.status === "failed") {
        toast.error(message || t("Sending failed"));
      } else {
        toast.success(t("Notification sent"));
      }
      setDraft(EMPTY_DRAFT);
      setShowErrors(false);
      onSent && onSent(notification);
    } catch (e) {
      toast.error(apiMessage(e, t("Something went wrong")));
    }
    setSending(false);
  };

  return (
    <div className="notif-composer" ref={topRef}>
      <section className="ui-card notif-form">
        <div className="notif-section">
          <span className="ui-label">{t("Type")}</span>
          <Segmented
            label={t("Type")}
            value={draft.kind}
            onChange={(kind) => set({ kind })}
            options={[
              { value: "simple", label: t("Simple notification") },
              { value: "video", label: t("Video notification") },
            ]}
          />
          <p className="ui-hint">
            {draft.kind === "video"
              ? t("Opens the video in the app, with its thumbnail.")
              : t("Text only: opens the app (or a link if you add one).")}
          </p>
        </div>

        {draft.kind === "video" && (
          <div className="notif-section">
            <span className="ui-label">{t("Video")}</span>
            <VideoPicker value={draft.video} onChange={pickVideo} />
          </div>
        )}

        <div className="notif-section ui-form">
          <div className="ui-field ui-field-full">
            <label htmlFor="notif-title">
              {t("Title")} <Counter value={draft.title} max={TITLE_MAX} />
            </label>
            <input
              id="notif-title"
              className="ui-input"
              value={draft.title}
              maxLength={120}
              placeholder={t("e.g. New episode tonight 🔥")}
              onChange={(e) => set({ title: e.target.value })}
            />
          </div>
          <div className="ui-field ui-field-full">
            <label htmlFor="notif-body">
              {t("Message")} <Counter value={draft.body} max={BODY_MAX} />
            </label>
            <textarea
              id="notif-body"
              className="ui-input notif-textarea"
              value={draft.body}
              maxLength={500}
              rows={3}
              placeholder={t("What do you want to tell your users?")}
              onChange={(e) => set({ body: e.target.value })}
            />
            {personalized && (
              <p className="ui-hint">
                {t("Tip: write {prenom} to insert each user's first name.")}
              </p>
            )}
          </div>

          <div className="ui-field ui-field-full">
            <Switch
              checked={draft.withEnglish}
              onChange={(withEnglish) => set({ withEnglish })}
              label={t("English version")}
              onLabel={t("English version for English-speaking users")}
              offLabel={t("English version for English-speaking users")}
            />
            {draft.withEnglish && draft.audienceType === "all" && (
              <p className="ui-hint">
                {t("Sent to all devices, the notification is in French only: the English version is used for targeted sends.")}
              </p>
            )}
          </div>
          {draft.withEnglish && (
            <>
              <div className="ui-field ui-field-full">
                <label htmlFor="notif-title-en">
                  {t("Title (English)")} <Counter value={draft.title_en} max={TITLE_MAX} />
                </label>
                <input
                  id="notif-title-en"
                  className="ui-input"
                  value={draft.title_en}
                  maxLength={120}
                  onChange={(e) => set({ title_en: e.target.value })}
                />
              </div>
              <div className="ui-field ui-field-full">
                <label htmlFor="notif-body-en">
                  {t("Message (English)")} <Counter value={draft.body_en} max={BODY_MAX} />
                </label>
                <textarea
                  id="notif-body-en"
                  className="ui-input notif-textarea"
                  value={draft.body_en}
                  maxLength={500}
                  rows={3}
                  onChange={(e) => set({ body_en: e.target.value })}
                />
              </div>
            </>
          )}

          <div className="ui-field">
            <label htmlFor="notif-image">
              {t("Image")} <span className="notif-optional">{t("optional")}</span>
            </label>
            <input
              id="notif-image"
              className="ui-input"
              value={draft.imageUrl}
              placeholder={draft.kind === "video" ? t("Video thumbnail by default") : "https://…"}
              onChange={(e) => set({ imageUrl: e.target.value })}
            />
          </div>
          {draft.kind === "simple" && (
            <div className="ui-field">
              <label htmlFor="notif-link">
                {t("Link opened on tap")} <span className="notif-optional">{t("optional")}</span>
              </label>
              <input
                id="notif-link"
                className="ui-input"
                value={draft.link}
                placeholder="https://afomedia.com/…"
                onChange={(e) => set({ link: e.target.value })}
              />
            </div>
          )}
        </div>

        <div className="notif-section">
          <span className="ui-label">{t("Recipients")}</span>
          <Segmented
            label={t("Recipients")}
            value={draft.audienceType}
            onChange={(audienceType) => set({ audienceType })}
            options={[
              { value: "all", label: t("All users") },
              { value: "users", label: t("Specific users") },
              { value: "segment", label: t("A group") },
            ]}
          />
          {draft.audienceType === "users" && <UserPicker value={draft.users} onChange={(users) => set({ users })} />}
          {draft.audienceType === "segment" && (
            <SegmentCriteria idPrefix="notif" value={draft} onChange={set} />
          )}
          <p className="notif-reach">
            <i className="ri-smartphone-line" aria-hidden="true"></i>
            {reachLabel}
          </p>
        </div>

        <div className="notif-section">
          <span className="ui-label">{t("When")}</span>
          <Segmented
            label={t("When")}
            value={draft.when}
            onChange={(when) => set({ when, scheduledAt: when === "later" && !draft.scheduledAt ? localInputValue(60) : draft.scheduledAt })}
            options={[
              { value: "now", label: t("Send now") },
              { value: "later", label: t("Schedule") },
            ]}
          />
          {draft.when === "later" && (
            <input
              type="datetime-local"
              className="ui-input notif-date"
              value={draft.scheduledAt}
              min={localInputValue(2)}
              aria-label={t("Sending date")}
              onChange={(e) => set({ scheduledAt: e.target.value })}
            />
          )}
          <div className="notif-inbox">
            <Switch
              checked={draft.spread}
              onChange={(spread) => set({ spread })}
              label={t("Spread the sending (expected high traffic)")}
              onLabel={t("Spread the sending (expected high traffic)")}
              offLabel={t("Spread the sending (expected high traffic)")}
            />
          </div>
          {draft.spread ? (
            <div className="notif-spread" role="status">
              <p>
                <i className="ri-timer-line" aria-hidden="true"></i> {spreadLabel}
              </p>
              <p className="ui-hint">
                {t("Each wave is a random mix of the audience, sized so that the people opening it stay within what the servers handle. You can stop the remaining waves from the history.")}
              </p>
              {draft.audienceType === "all" && (
                <p className="ui-hint">
                  {t("To everyone, the waves go to the devices of logged-in users; devices without an account do not get the push, but the notification is in the app's list for everyone.")}
                </p>
              )}
            </div>
          ) : (
            <p className="ui-hint notif-spread-off">{t("Off: everyone receives it at once (usual sending).")}</p>
          )}
          <div className="notif-inbox">
            <Switch
              checked={draft.saveToInbox}
              onChange={(saveToInbox) => set({ saveToInbox })}
              label={t("Also show it in the app's notification list")}
              onLabel={t("Also show it in the app's notification list")}
              offLabel={t("Also show it in the app's notification list")}
            />
          </div>
        </div>

        <div className="notif-actions">
          {showErrors && error && (
            <p className="ui-hint" data-error="">
              {t(error)}
            </p>
          )}
          <button type="button" className="ui-btn ui-btn-secondary" onClick={() => { setDraft(EMPTY_DRAFT); setShowErrors(false); }}>
            {t("Reset")}
          </button>
          <button type="button" className="ui-btn" disabled={sending} onClick={submit}>
            <i className={draft.when === "later" ? "ri-calendar-2-line" : "ri-send-plane-fill"} aria-hidden="true"></i>
            {sending ? t("Sending…") : draft.when === "later" ? t("Schedule") : t("Send")}
          </button>
        </div>
      </section>

      <aside className="notif-preview-col">
        <PhonePreview
          title={draft.title.replace(/\{(prenom|prénom|name)\}/gi, "Awa")}
          body={draft.body.replace(/\{(prenom|prénom|name)\}/gi, "Awa")}
          image={previewImage}
        />
        <p className="ui-hint notif-preview-hint">
          {t("Preview: the exact look depends on the phone (iOS / Android).")}
        </p>
      </aside>

      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={send}
        confirmLabel={draft.when === "later" ? t("Schedule") : t("Send")}
      >
        <strong>{draft.when === "later" ? t("Schedule this notification?") : t("Send this notification now?")}</strong>
        <p>
          {reachLabel}
          {draft.when === "later" && draft.scheduledAt ? ` · ${new Date(draft.scheduledAt).toLocaleString()}` : ""}
        </p>
        {draft.spread && <p>{spreadLabel}</p>}
        {draft.when === "now" && <p className="ui-hint">{t("A sent notification cannot be recalled.")}</p>}
      </ConfirmDialog>
    </div>
  );
};

export default Composer;
